/**
 * One tech's every mapping, opened under their row on Mappings → Techs (the
 * owner, 2026-10-19: "the techs need the drop down like clients as their
 * fields are getting crammed in"), as a client's opens under its row
 * (ClientPanel.tsx): their Autotask resource and its default role, their
 * SkySwitch extension, their ScreenConnect name and work computer, their Datto
 * RMM user, their mailbox and its Read mail switch, their office addresses,
 * the internet addresses their sign-ins came from (read only), and their VPN
 * addresses, as learned (ADR 0179), each in its own section.
 *
 * Each change saves at once, and the API checks it again. Each field is
 * described by the tech's name in the row above it.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import type {
  AutotaskResource,
  OfficeAddress,
  ScreenConnectMachine,
  SkySwitchExtension,
  Tech,
  TechMapping,
  VpnAddress,
  WorkComputer,
} from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Combobox } from "../../components/ui/Combobox";
import { formatDateTimeIn, formatTimeIn, todayIn } from "../../lib/dates";
import { possessive } from "../../lib/possessive";
import { TECH_MAPPING_KEY } from "./keys";
import { sectionTitle } from "./summaryRows";
import { userName } from "./techStatus";

/** A field's own label in a section, under the section's heading. */
const fieldTitle = "text-xs font-semibold text-muted";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.fieldErrors[0]?.message ?? error.message;
  }
  return error instanceof Error ? error.message : "The change was not saved.";
}

function resourceLabel(resource: AutotaskResource): string {
  return `${resource.name} · ${String(resource.autotask_resource_id)}`;
}

/**
 * Which Autotask resource the user is (BUILD_PLAN.md sections 5.1 and 12.3),
 * in one field like every other mapping (ADR 0078; #285), and its default
 * role, read only, from Autotask: what time entries use (section 10.3). A
 * resource with the user's email is suggested, never applied: the owner
 * chooses by ID (section 8.1).
 */
function AutotaskSection({
  tech,
  mapping,
  labelledBy,
  onSaved,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
  onSaved: (resourceId: number | null) => void;
}) {
  const { api } = useSession();
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (resourceId: number | null) => api.setAutotaskResource(tech.id, resourceId),
    onSuccess: (result) => {
      setError(null);
      onSaved(result.autotask_resource_id);
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send, saving } = useOneSaveAtATime(save);

  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const current = mapping.resources.find(
    (r) => r.autotask_resource_id === tech.autotask_resource_id,
  );
  const suggested =
    tech.suggested_resource_id === null
      ? undefined
      : mapping.resources.find((r) => r.autotask_resource_id === tech.suggested_resource_id);
  const offered = mapping.resources.filter(
    (r) => r.available || r.autotask_resource_id === tech.autotask_resource_id,
  );
  const options = [
    // Mapped, but no longer among the synced resources: it stays, as it is.
    ...(tech.autotask_resource_id !== null && current === undefined
      ? [
          {
            value: String(tech.autotask_resource_id),
            label: `${String(tech.autotask_resource_id)} (not in Autotask)`,
          },
        ]
      : []),
    ...offered.map((resource) => {
      const others = resource.mapped_user_id !== null && resource.mapped_user_id !== tech.id;
      return {
        value: String(resource.autotask_resource_id),
        label: resourceLabel(resource),
        detail: others
          ? possessive(names.get(resource.mapped_user_id ?? "") ?? "someone else")
          : resource.available
            ? undefined
            : "can't be used",
        disabled: others,
      };
    }),
  ];
  // Mapped to a resource that can't be used: the field is invalid, and why.
  const flagged = current !== undefined && !current.available;
  const flaggedId = `${id}-flagged`;

  return (
    <section aria-label="Autotask" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Autotask</h4>
      <span aria-hidden="true" className={fieldTitle}>
        Resource
      </span>
      <Combobox
        label="Autotask resource"
        hideLabel
        // "Invalid" is read with its reason, not on its own (#285).
        describedBy={flagged ? `${labelledBy} ${flaggedId}` : labelledBy}
        options={options}
        // What's being saved, while it saves; then what's saved.
        value={String((saving ? saving.value : tech.autotask_resource_id) ?? "")}
        invalid={flagged}
        placeholder="Not mapped"
        onChange={(value) => {
          send(value === "" ? null : Number(value));
        }}
      />
      {flagged ? (
        <p
          id={flaggedId}
          className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
        >
          Needs attention: this resource is inactive or no longer in Autotask.
        </p>
      ) : null}
      {suggested !== undefined ? (
        <button
          type="button"
          className="min-h-9 cursor-pointer self-start rounded-row border border-line bg-card px-3 py-1.5 text-left text-sm font-medium hover:bg-brand-tint"
          onClick={() => {
            send(suggested.autotask_resource_id);
          }}
        >
          {`Use ${resourceLabel(suggested)} (same email)`}
        </button>
      ) : null}
      {error !== null ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not saved: {error}
        </p>
      ) : null}
      <div className="mt-1 flex flex-col gap-1">
        <span className={fieldTitle}>Default role (from Autotask)</span>
        {current === undefined ? (
          <span className="text-muted">—</span>
        ) : current.default_role ? (
          <span>
            {current.default_role.name}{" "}
            <span className="font-mono text-sm text-muted">
              {current.default_role.autotask_role_id}
            </span>
          </span>
        ) : (
          <span className="text-status-needs-client-fg">
            No default role in Autotask. Set one on the resource in Autotask.
          </span>
        )}
      </div>
    </section>
  );
}

function extensionLabel(extension: SkySwitchExtension): string {
  const name = extension.name ? ` · ${extension.name}` : "";
  const group = extension.group ? ` (${extension.group})` : "";
  return `${extension.extension}${name}${group}`;
}

/**
 * A tech's SkySwitch extension: one (ADR 0078, superseding ADR 0028's
 * several), in one field, with when it was last on a call. Another tech's
 * extension is listed greyed. The extension with the tech's name is offered,
 * applied only when asked (ADR 0028). A tech with several from before is
 * flagged until one is chosen, and choosing keeps just that one.
 */
export function SkySwitchSection({
  tech,
  mapping,
  labelledBy,
  self = false,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
  /** On My settings: said to the person themselves, and whom to ask (#833). */
  self?: boolean;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (keys: string[]) => api.setSkySwitchExtensions(tech.id, keys),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const byKey = new Map(mapping.extensions.map((e) => [e.key, e]));
  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const mine = tech.skyswitch_extensions;
  const several = mine.length > 1;
  const value = mine.length === 1 ? (mine[0] ?? "") : "";
  const options = mapping.extensions
    .filter((e) => e.available || e.mapped_user_id === tech.id)
    .map((e) => {
      const others = e.mapped_user_id !== null && e.mapped_user_id !== tech.id;
      return {
        value: e.key,
        label: extensionLabel(e),
        detail: others
          ? possessive(names.get(e.mapped_user_id ?? "") ?? "someone else")
          : e.available
            ? undefined
            : "not in SkySwitch",
        disabled: others,
      };
    });
  const label = (key: string) => {
    const extension = byKey.get(key);
    return extension ? extensionLabel(extension) : key;
  };
  // Mapped, but no longer in SkySwitch's list: it may stay, flagged.
  const gone = mine.filter((key) => byKey.get(key)?.available !== true);
  // Only extensions in SkySwitch's list have calls to look at (section 12.3).
  const known = mine.flatMap((key) => byKey.get(key) ?? []);

  return (
    <section aria-label="SkySwitch extension" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>SkySwitch extension</h4>
      {mapping.extensions.length === 0 && mine.length === 0 ? (
        <span className="text-sm text-muted">
          {self
            ? // Only those in their name are offered them (#850).
              "No extension in your name in SkySwitch: ask an owner to map yours."
            : "No extensions synced yet. They're copied every night at 07:15 UTC; Owner tools → SkySwitch → Copy SkySwitch's extensions copies them now."}
        </span>
      ) : (
        <>
          <Combobox
            label="SkySwitch extension"
            hideLabel
            describedBy={labelledBy}
            options={options}
            value={value}
            placeholder={several ? "Choose the one to keep" : "Not mapped"}
            onChange={(key) => {
              send(key === "" ? [] : [key]);
            }}
          />
          {several ? (
            <p className="text-sm text-status-needs-client-fg">
              {`${String(mine.length)} extensions from before (${mine.map(label).join(", ")}). One per tech now: choose the one to keep.`}
            </p>
          ) : null}
          {gone.map((key) => (
            <p key={key} className="text-sm text-status-needs-client-fg">
              {`${label(key)} is no longer in SkySwitch.`}
            </p>
          ))}
          {known.length > 0 ? (
            <ul aria-label="When each extension was last on a call" className="text-sm text-muted">
              {known.map((extension) => (
                <li key={extension.key}>
                  {`${extension.extension}: ${
                    extension.last_seen_at !== null
                      ? `seen ${formatDateTimeIn(extension.last_seen_at, me.timezone)}`
                      : "not seen in the last 7 days"
                  }`}
                </li>
              ))}
            </ul>
          ) : null}
          {mine.length === 0
            ? tech.suggested_extensions.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="h-9 cursor-pointer self-start rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint"
                  onClick={() => {
                    send([key]);
                  }}
                >
                  {`Use ${label(key)} (same name)`}
                </button>
              ))
            : null}
          {error !== null ? (
            <p role="alert" className="text-sm text-status-needs-client-fg">
              Not saved: {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * The name ScreenConnect shows for the user (roadmap PR-38; ADR 0070), in one
 * field (ADR 0078): the names seen in the last 7 days are listed with when
 * (section 12.3), another user's greyed, and a name not seen yet can be typed.
 * Choosing saves it, and × clears it.
 */
function ScreenConnectName({
  tech,
  mapping,
  labelledBy,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (username: string | null) => api.setScreenConnectUsername(tech.id, username),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const options = mapping.screenconnect_names.map((n) => {
    const others = n.mapped_user_id !== null && n.mapped_user_id !== tech.id;
    return {
      value: n.name,
      label: n.name,
      detail: others
        ? possessive(names.get(n.mapped_user_id ?? "") ?? "someone else")
        : `seen ${formatDateTimeIn(n.last_seen, me.timezone)}`,
      disabled: others,
    };
  });

  return (
    <div className="flex flex-col gap-2">
      <span aria-hidden="true" className={fieldTitle}>
        Name
      </span>
      <Combobox
        label="ScreenConnect name"
        hideLabel
        describedBy={labelledBy}
        options={options}
        value={tech.screenconnect_username ?? ""}
        allowTyped
        placeholder="Not mapped"
        onChange={(name) => {
          send(name === "" ? null : name);
        }}
      />
      {tech.screenconnect_username !== null ? (
        <p className="text-sm text-muted">
          {tech.screenconnect_last_seen !== null
            ? `Seen ${formatDateTimeIn(tech.screenconnect_last_seen, me.timezone)}`
            : "Not seen in the last 7 days"}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not saved: {error}
        </p>
      ) : null}
    </div>
  );
}

function machineDetail(machine: ScreenConnectMachine, timeZone: string): string {
  const when = formatDateTimeIn(machine.last_connected_at, timeZone);
  return machine.participants.length > 0 ? `${machine.participants.join(", ")} · ${when}` : when;
}

function workComputerName(computer: WorkComputer): string {
  return computer.machine_name ?? computer.session_id;
}

/**
 * The tech's work computer (the owner, 2026-10-16; ADR 0162): their own
 * computer at the office, whose ScreenConnect sessions don't count, so they make
 * no blocks to dismiss. Picked from the machines connected to in the last 30
 * days (Access sessions), with who connected and when; another tech's greyed.
 * It's the session that's kept, so renaming the machine changes nothing. Each
 * shows its name and when it was last connected to, and × takes it away.
 * Usually one; a second (a laptop) can be added.
 */
function WorkComputers({
  tech,
  mapping,
  labelledBy,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (sessionIds: string[]) => api.setWorkComputers(tech.id, sessionIds),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const mine = tech.work_computers.map((w) => w.session_id);
  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const options = mapping.screenconnect_machines
    .filter((m) => !mine.includes(m.session_id))
    .map((m) => {
      const others = m.mapped_user_id !== null && m.mapped_user_id !== tech.id;
      return {
        value: m.session_id,
        label: m.machine_name || m.session_id,
        detail: others
          ? possessive(names.get(m.mapped_user_id ?? "") ?? "someone else")
          : machineDetail(m, me.timezone),
        disabled: others,
      };
    });

  return (
    <div className="flex flex-col gap-2">
      <span aria-hidden="true" className={fieldTitle}>
        Work computer
      </span>
      {tech.work_computers.length > 0 ? (
        <ul aria-label={`${possessive(userName(tech))} work computers`} className="flex flex-col gap-1.5">
          {tech.work_computers.map((w) => (
            <li
              key={w.session_id}
              className="flex items-start gap-2 rounded-row border border-line px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="block font-medium">{workComputerName(w)}</span>
                <span className="block text-muted">
                  {w.last_connected_at !== null
                    ? `Connected ${formatDateTimeIn(w.last_connected_at, me.timezone)}`
                    : "Not connected to in the last 30 days"}
                </span>
              </span>
              <button
                type="button"
                aria-label={`Remove ${workComputerName(w)}`}
                className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint"
                onClick={() => {
                  send(mine.filter((id) => id !== w.session_id));
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Combobox
        label="Work computer"
        hideLabel
        describedBy={labelledBy}
        options={options}
        value=""
        placeholder={mine.length > 0 ? "Add another" : "Not mapped"}
        onChange={(sessionId) => {
          if (sessionId !== "") {
            send([...mine, sessionId]);
          }
        }}
      />
      {error !== null ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not saved: {error}
        </p>
      ) : null}
    </div>
  );
}

/** The tech's ScreenConnect name, and under it their work computer. */
export function ScreenConnectSection({
  tech,
  mapping,
  labelledBy,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
}) {
  return (
    <section aria-label="ScreenConnect" className="flex flex-col gap-3">
      <h4 className={sectionTitle}>ScreenConnect</h4>
      <ScreenConnectName tech={tech} mapping={mapping} labelledBy={labelledBy} />
      <WorkComputers tech={tech} mapping={mapping} labelledBy={labelledBy} />
    </section>
  );
}

/**
 * The tech's one Datto RMM user (roadmap PR-49; ADR 0080), from the worker's
 * nightly copy of Datto's users: one field, another tech's greyed, one
 * disabled or gone in Datto greyed too. The user with the tech's email is
 * offered, applied only when asked.
 */
export function DattoSection({
  tech,
  mapping,
  labelledBy,
  self = false,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
  /** On My settings: said to the person themselves, and whom to ask (#833). */
  self?: boolean;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (username: string | null) => api.setDattoRmmUsername(tech.id, username),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the field still usable (#241).
  const { send } = useOneSaveAtATime(save);
  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const options = mapping.datto_users.map((u) => {
    const others = u.mapped_user_id !== null && u.mapped_user_id !== tech.id;
    return {
      value: u.username,
      label: u.name ? `${u.username} · ${u.name}` : u.username,
      detail: others
        ? possessive(names.get(u.mapped_user_id ?? "") ?? "someone else")
        : u.available
          ? (u.email ?? undefined)
          : "not active in Datto",
      disabled: others || (!u.available && u.username !== tech.datto_rmm_username),
    };
  });
  const suggested = tech.suggested_datto_username;

  return (
    <section aria-label="Datto RMM user" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Datto RMM user</h4>
      {mapping.datto_users.length === 0 && tech.datto_rmm_username === null ? (
        <span className="text-sm text-muted">
          {self
            ? // Only those at their email are offered them (#850).
              "No Datto RMM user at your email: ask an owner to map yours."
            : "No Datto users copied yet. With the worker's Datto values set (datto-rmm.md step 4), they're copied every night at 07:21 UTC; Owner tools → Datto RMM → Copy Datto RMM's users copies them now."}
        </span>
      ) : (
        <>
          <Combobox
            label="Datto RMM user"
            hideLabel
            describedBy={labelledBy}
            options={options}
            value={tech.datto_rmm_username ?? ""}
            placeholder="Not mapped"
            onChange={(username) => {
              send(username === "" ? null : username);
            }}
          />
          {tech.datto_rmm_username !== null ? (
            <p className="text-sm text-muted">
              {tech.datto_rmm_last_seen !== null
                ? `Seen ${formatDateTimeIn(tech.datto_rmm_last_seen, me.timezone)}`
                : "Not seen in the last 7 days"}
            </p>
          ) : null}
          {suggested !== null && tech.datto_rmm_username === null ? (
            <button
              type="button"
              className="h-9 cursor-pointer self-start rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint"
              onClick={() => {
                send(suggested);
              }}
            >
              {`Use ${suggested} (same email)`}
            </button>
          ) : null}
          {error !== null ? (
            <p role="alert" className="text-sm text-status-needs-client-fg">
              Not saved: {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * The tech's mail (roadmap PR-55; ADR 0108 decision 3). Their mailbox is the
 * address they sign in with, read only: nothing is picked (ADR 0109). The
 * switch to read it is off until the owner turns it on, and Exchange's group
 * must let the app in too. Once on, when mail was last seen, or that Exchange
 * refused it.
 */
export function MailSection({
  tech,
  labelledBy,
  self = false,
}: {
  tech: Tech;
  labelledBy: string;
  /** On My settings: said to the person themselves, and whom to ask (#833). */
  self?: boolean;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (readMail: boolean) => api.setReadMail(tech.id, readMail),
    onSuccess: () => {
      setError(null);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setError(errorMessage(e));
    },
  });
  // One save at a time, with the switch still usable (#241).
  const { send, saving } = useOneSaveAtATime(save);
  const on = saving ? saving.value : tech.read_mail;
  const statusId = `${id}-status`;

  return (
    <section aria-label="Microsoft 365 mailbox" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Microsoft 365 mailbox</h4>
      {tech.email === null && !tech.read_mail ? (
        <span className="text-sm text-muted">
          No mailbox yet: their sign-in hasn&apos;t given an email address.
        </span>
      ) : (
        <>
          {tech.email !== null ? (
            <span className="max-w-full min-w-0 self-start rounded-input bg-brand-tint px-2.5 py-0.5 text-sm break-all text-brand-ink-on-tint">
              <span className="sr-only">Mailbox: </span>
              {tech.email}
            </span>
          ) : null}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={on}
              aria-describedby={tech.read_mail ? `${labelledBy} ${statusId}` : labelledBy}
              onChange={(event) => {
                send(event.target.checked);
              }}
              className="size-4 accent-brand"
            />
            Read mail
          </label>
          {tech.read_mail ? (
            tech.mail_refused_at !== null ? (
              <div id={statusId} className="text-sm">
                <p className="text-status-needs-client-fg">Refused: not in the techs&apos; group</p>
                {self ? (
                  <p className="text-muted">Exchange won&apos;t let Axon Time read your mail yet: ask an owner to add you.</p>
                ) : (
                  <p className="text-muted">
                    Add them to <span className="font-mono">sg-axon-time-techs</span> (m365.md step 3).
                  </p>
                )}
              </div>
            ) : (
              <p id={statusId} className="text-sm text-muted">
                {tech.mail_last_seen !== null
                  ? `Seen ${formatDateTimeIn(tech.mail_last_seen, me.timezone)}`
                  : "Not seen in the last 7 days"}
              </p>
            )
          ) : null}
          {tech.read_mail && !tech.is_active ? (
            <p className="text-sm text-muted">Not read while their account is disabled.</p>
          ) : null}
          {error !== null ? (
            <p role="alert" className="text-sm text-status-needs-client-fg">
              Not saved: {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * The addresses a tech's office computer keeps (Phase 7; ADR 0158 decisions 4
 * and 10), by which the SonicWall's connection logs name them: one address, or
 * a small range, typed by the owner. × takes one away from now; the API keeps
 * when it was theirs, and refuses one another tech holds, saying whose. Their
 * work computer's LAN address, read from ScreenConnect (ADR 0162), is listed
 * too, "From ScreenConnect", with no ×: it follows the computer.
 */
export function OfficeAddressesSection({
  tech,
  labelledBy,
  self = false,
}: {
  tech: Tech;
  labelledBy: string;
  /** On My settings: said to the person themselves, and whom to ask (#833). */
  self?: boolean;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const inputId = useId();
  const alertId = useId();
  const [typed, setTyped] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const name = userName(tech);
  const add = useMutation({
    mutationFn: (address: string) => api.addOfficeAddress(tech.id, address),
    onSuccess: (added) => {
      setTyped("");
      setRefused(null);
      setDone(`Added ${added.address}.`);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not added: ${errorMessage(e)}`);
    },
  });
  const remove = useMutation({
    mutationFn: (address: OfficeAddress) => api.endOfficeAddress(tech.id, address.id),
    onSuccess: (_, address) => {
      setRefused(null);
      setDone(`Removed ${address.address}.`);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not removed: ${errorMessage(e)}`);
    },
  });

  return (
    <section aria-label="Office addresses" className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Office addresses</h4>
      {self ? (
        <p className="text-sm text-muted">
          Your computer&apos;s address on the office network, so the office firewall&apos;s logs know your work.
          Your work computer&apos;s appears here by itself; add one only for another computer you use at the office
          (ipconfig shows its address).
        </p>
      ) : null}
      {tech.office_addresses.length === 0 ? (
        <p className="text-sm text-muted">None yet.</p>
      ) : (
        <ul aria-label={`${possessive(name)} office addresses`} className="flex flex-col gap-1.5">
          {tech.office_addresses.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-row border border-line px-3 py-1.5 text-sm">
              <span className="min-w-0 font-mono [overflow-wrap:anywhere]">{a.address}</span>
              {a.source === "screenconnect" ? (
                // Their work computer's LAN address (ADR 0162): it follows the
                // computer, so it isn't removed here.
                <span className="ml-auto rounded-pill bg-brand-tint px-2 py-0.5 text-xs font-medium text-brand-ink-on-tint">
                  From ScreenConnect
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Remove ${a.address}`}
                  disabled={remove.isPending}
                  className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint disabled:opacity-50"
                  onClick={() => {
                    remove.mutate(a);
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (typed.trim() !== "") {
            add.mutate(typed.trim());
          }
        }}
      >
        <label htmlFor={inputId} className="sr-only">
          {`Add an office address for ${name}`}
        </label>
        <input
          id={inputId}
          type="text"
          placeholder="192.0.2.10"
          autoComplete="off"
          spellCheck={false}
          value={typed}
          aria-invalid={refused === null ? undefined : true}
          aria-describedby={refused === null ? labelledBy : `${labelledBy} ${alertId}`}
          className="h-10 w-full min-w-0 flex-[1_1_120px] rounded-input border border-line bg-card px-3 font-mono text-sm text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          onChange={(event) => {
            setTyped(event.target.value);
            setRefused(null);
            setDone(null);
          }}
        />
        <button
          type="submit"
          disabled={add.isPending || typed.trim() === ""}
          className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint disabled:cursor-default disabled:opacity-50"
        >
          Add
        </button>
      </form>
      {refused !== null ? (
        <p id={alertId} role="alert" className="text-sm text-status-needs-client-fg">
          {refused}
        </p>
      ) : null}
      <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
        {done ?? ""}
      </p>
    </section>
  );
}

/**
 * Where the tech's signed-in requests came from in the last 30 days (ADR 0176;
 * roadmap PR-81), as the proxies in front of Axon Time saw them, never as a
 * browser claimed: read only, the latest seen first, each with when it was
 * first and last seen. The owner's alone: the API refuses everyone else.
 * v0.8.0's VPN work will recognize their Pritunl tunnels by them; nothing uses
 * them yet, and the panel says so.
 */
function InternetAddressesSection({ tech }: { tech: Tech }) {
  const { me } = useSession();
  const noteId = useId();

  return (
    <section aria-label="Internet addresses" aria-describedby={noteId} className="flex flex-col gap-2">
      <h4 className={sectionTitle}>Internet addresses</h4>
      <span aria-hidden="true" className={fieldTitle}>
        From their sign-ins
      </span>
      {tech.internet_addresses.length === 0 ? (
        <p className="text-sm text-muted">None in the last 30 days.</p>
      ) : (
        <ul
          aria-label={`${possessive(userName(tech))} internet addresses, from their sign-ins`}
          className="flex flex-col gap-1.5"
        >
          {tech.internet_addresses.map((a) => (
            <li key={a.address} className="rounded-row border border-line px-3 py-1.5 text-sm">
              <span className="block min-w-0 font-mono [overflow-wrap:anywhere]">{a.address}</span>
              <span className="block text-muted">
                {`First seen ${formatDateTimeIn(a.first_seen_at, me.timezone)}`}
              </span>
              <span className="block text-muted">
                {`Last seen ${formatDateTimeIn(a.last_seen_at, me.timezone)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p id={noteId} className="text-sm text-muted">
        Each is kept 30 days after its last use. Their VPN tunnels from these are recognized, and their VPN
        addresses learned from them.
      </p>
    </section>
  );
}

/** How a VPN address came to be the tech's (ADR 0179). */
function vpnHow(address: VpnAddress): string {
  if (address.how === "owner") {
    return "Set on this page";
  }
  const how = address.how === "startup" ? "start-up timing" : "overlap";
  const sessions = address.learned_sessions ?? address.sessions;
  return `Learned by ${how}, from ${String(sessions)} ${sessions === 1 ? "session" : "sessions"}`;
}

/** What a flag says happened, and when (ADR 0159 decision 4). */
function vpnFlag(address: VpnAddress, timeZone: string): string | null {
  if (address.conflict === null || address.conflict_at === null) {
    return null;
  }
  const when = formatDateTimeIn(address.conflict_at, timeZone);
  const what =
    address.conflict === "no_tunnel"
      ? "with no one's tunnel up"
      : address.conflict === "unknown_tunnel"
        ? "while their tunnel was down, and someone was connected from an address no one has used Axon Time from"
        : "while their tunnel was down";
  return `Active ${when} ${what}. None of its work counts until you confirm it or clear it.`;
}

/**
 * How far learning has got (its job's watermark): every moment before it is
 * checked. Today's as a time, "4:07 PM"; an earlier day's with its date.
 */
function checkedUpTo(through: string, timeZone: string): string {
  return todayIn(timeZone, new Date(through)) === todayIn(timeZone)
    ? formatTimeIn(through, timeZone)
    : formatDateTimeIn(through, timeZone);
}

/**
 * Whose an address is, or what it fits, for the list to set one from. One no
 * one's yet is still being learned, and says how far that has got; one no
 * tech fits isn't (ADR 0179).
 */
function vpnChoiceDetail(
  candidates: readonly string[] | null,
  names: Map<string, string>,
  learning: string | null,
): string {
  if (candidates !== null && candidates.length === 0) {
    return "No one tech fits";
  }
  const fits =
    candidates === null
      ? "Not narrowed yet"
      : `Fits ${candidates.map((id) => names.get(id) ?? "someone").join(" or ")}`;
  return `${fits}. Still learning${learning === null ? "" : `: checked up to ${learning}`}`;
}

/** One address and nothing else, as the API takes it: a quick check while typing. */
function looksLikeAnAddress(typed: string): boolean {
  const text = typed.trim();
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(text) || (/^[0-9a-fA-F:.]+$/.test(text) && text.includes(":"));
}

/**
 * The tech's VPN addresses (ADR 0159 decisions 3, 4 and 11; ADR 0179), as the
 * worker learned them from their Pritunl tunnels: each with how (overlap, or
 * start-up timing), from how many sessions, when, and when it last fit. One
 * on each of Pritunl's networks, usually: OpenVPN's and WireGuard's.
 *
 * - A flag says it was active while their tunnel was down: its work counts for
 *   no one until it's confirmed (set again) or cleared.
 * - The owner sets one from the addresses seen (no one's yet, or another
 *   tech's, which moves here), or types one as Pritunl shows it.
 * - × clears one: it's learned again from its next activity.
 * - One set here isn't relearned, and is still checked.
 *
 * The owner's alone: the API refuses everyone else.
 */
export function VpnAddressesSection({
  tech,
  mapping,
  labelledBy,
  self = false,
}: {
  tech: Tech;
  mapping: TechMapping;
  labelledBy: string;
  /** On My settings: said to the person themselves, and whom to ask (#833). */
  self?: boolean;
}) {
  const { api, me } = useSession();
  const queryClient = useQueryClient();
  const noteId = useId();
  const [refused, setRefused] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const name = userName(tech);
  const names = new Map(mapping.techs.map((t) => [t.id, userName(t)]));
  const set = useMutation({
    mutationFn: (address: string) => api.setVpnAddress(tech.id, address),
    onSuccess: (saved) => {
      setRefused(null);
      setDone(`Set ${saved.address}.`);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not set: ${errorMessage(e)}`);
    },
  });
  const clear = useMutation({
    mutationFn: (address: VpnAddress) => api.clearVpnAddress(tech.id, address.id),
    onSuccess: (_, address) => {
      setRefused(null);
      setDone(`Cleared ${address.address}: it's learned again from its next activity.`);
      return queryClient.invalidateQueries({ queryKey: TECH_MAPPING_KEY });
    },
    onError: (e) => {
      setDone(null);
      setRefused(`Not cleared: ${errorMessage(e)}`);
    },
  });
  const busy = set.isPending || clear.isPending;
  const learning =
    mapping.vpn_learning_through === null ? null : checkedUpTo(mapping.vpn_learning_through, me.timezone);
  const options = [
    ...mapping.unsettled_vpn_addresses.map((a) => ({
      value: a.address,
      label: a.address,
      detail: vpnChoiceDetail(a.candidate_user_ids, names, learning),
    })),
    ...mapping.techs
      .filter((t) => t.id !== tech.id)
      .flatMap((t) =>
        t.vpn_addresses.map((a) => ({
          value: a.address,
          label: a.address,
          detail: `${possessive(userName(t))}: moves here`,
        })),
      ),
  ];

  return (
    <section aria-label="VPN addresses" aria-describedby={noteId} className="flex flex-col gap-2">
      <h4 className={sectionTitle}>VPN addresses</h4>
      <span aria-hidden="true" className={fieldTitle}>
        Learned from their VPN tunnels
      </span>
      {tech.vpn_addresses.length === 0 ? (
        <p className="text-sm text-muted">
          {`None learned yet. Addresses appear about 5 minutes after the VPN work${
            learning === null ? "" : `; learning has checked up to ${learning}`
          }.`}
        </p>
      ) : (
        <ul aria-label={`${possessive(name)} VPN addresses`} className="flex flex-col gap-1.5">
          {tech.vpn_addresses.map((a) => {
            const flag = vpnFlag(a, me.timezone);
            return (
              <li key={a.id} className="flex items-start gap-2 rounded-row border border-line px-3 py-1.5 text-sm">
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  <span className="block font-mono">{a.address}</span>
                  <span className="block text-muted">{vpnHow(a)}</span>
                  {a.learned_at !== null ? (
                    <span className="block text-muted">
                      {`${a.how === "owner" ? "Set" : "Learned"} ${formatDateTimeIn(a.learned_at, me.timezone)}`}
                    </span>
                  ) : null}
                  {a.confirmed_at !== null ? (
                    <span className="block text-muted">{`Last fit ${formatDateTimeIn(a.confirmed_at, me.timezone)}`}</span>
                  ) : null}
                  {flag !== null ? (
                    <span className="mt-1 block rounded-row bg-status-needs-client-bg px-2 py-1 text-status-needs-client-fg">
                      {flag}
                    </span>
                  ) : null}
                  {flag !== null ? (
                    <button
                      type="button"
                      disabled={busy}
                      className="mt-1 min-h-9 cursor-pointer rounded-row border border-line bg-card px-3 py-1.5 text-left text-sm font-medium hover:bg-brand-tint disabled:opacity-50"
                      onClick={() => {
                        set.mutate(a.address);
                      }}
                    >
                      {`Confirm ${a.address} is ${possessive(name)}`}
                    </button>
                  ) : null}
                </span>
                <button
                  type="button"
                  aria-label={`Clear ${a.address}`}
                  disabled={busy}
                  className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint disabled:opacity-50"
                  onClick={() => {
                    clear.mutate(a);
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Combobox
        label={`Set a VPN address for ${name}`}
        hideLabel
        describedBy={labelledBy}
        options={options}
        value=""
        disabled={busy}
        allowTyped={looksLikeAnAddress}
        invalid={refused !== null}
        placeholder={tech.vpn_addresses.length > 0 ? "Set another" : "Set one"}
        onChange={(address) => {
          if (address !== "") {
            set.mutate(address.trim());
          }
        }}
      />
      {refused !== null ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          {refused}
        </p>
      ) : null}
      <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
        {done ?? ""}
      </p>
      <p id={noteId} className="text-sm text-muted">
        {self
          ? "Learned from your VPN tunnels as you work, or set here: the address the VPN app shows you. One you set stays yours, and is still checked."
          : "Learned from when each is active and whose tunnel is up then, or from coming back within a minute of their tunnel starting. Setting another tech's moves it here. One you set isn't relearned, but is still checked."}
      </p>
    </section>
  );
}

export function TechDetails({
  id,
  tech,
  mapping,
  labelledBy,
  onSaved,
}: {
  id: string;
  tech: Tech;
  mapping: TechMapping;
  /** The tech's name in their row: what describes each field. */
  labelledBy: string;
  onSaved: (resourceId: number | null) => void;
}) {
  return (
    <div
      id={id}
      role="region"
      aria-label={userName(tech)}
      className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]"
    >
      <AutotaskSection tech={tech} mapping={mapping} labelledBy={labelledBy} onSaved={onSaved} />
      <SkySwitchSection tech={tech} mapping={mapping} labelledBy={labelledBy} />
      <ScreenConnectSection tech={tech} mapping={mapping} labelledBy={labelledBy} />
      <DattoSection tech={tech} mapping={mapping} labelledBy={labelledBy} />
      <MailSection tech={tech} labelledBy={labelledBy} />
      <OfficeAddressesSection tech={tech} labelledBy={labelledBy} />
      <InternetAddressesSection tech={tech} />
      <VpnAddressesSection tech={tech} mapping={mapping} labelledBy={labelledBy} />
    </div>
  );
}
